import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import { User, ClipboardList, Briefcase, AlertCircle, GraduationCap, TrendingUp, CheckCircle, Clock, ChevronLeft, ChevronRight, Bell, Star, XCircle, Eye, ArrowRight } from 'lucide-react'
import type { ApplicationStatus } from '../types'

interface AppUpdate {
  id: string
  job_title: string
  company_name: string
  status: ApplicationStatus
  reviewed_at: string
}

const appUpdateConfig: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  reviewed:    { label: 'Under Review', color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-400',    icon: <Eye         className="w-4 h-4" /> },
  shortlisted: { label: 'Interview',    color: 'text-green-700',   bg: 'bg-green-50',   border: 'border-green-500',   icon: <Star        className="w-4 h-4" /> },
  hired:       { label: 'Hired!',       color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-500', icon: <CheckCircle className="w-4 h-4" /> },
  rejected:    { label: 'Rejected',     color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-400',     icon: <XCircle     className="w-4 h-4" /> },
}

interface DashboardStats {
  surveysCompleted: number
  completedEligibleSurveys: number
  totalEligibleSurveys: number
  surveysPending: number
  lastLogin: string
  yearsSinceGraduation: number
  activeApplications: number
}

interface RecentActivity {
  id: string
  description: string
  timestamp: string
}

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    surveysCompleted: 0,
    completedEligibleSurveys: 0,
    totalEligibleSurveys: 0,
    surveysPending: 0,
    lastLogin: 'Today',
    yearsSinceGraduation: 0,
    activeApplications: 0
  })
  const [activities, setActivities] = useState<RecentActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [profileData, setProfileData] = useState<any>(null)
  const [pendingSurveyList, setPendingSurveyList] = useState<{ id: string; title: string; deadline: string | null }[]>([])
  const [applicationUpdates, setApplicationUpdates] = useState<AppUpdate[]>([])
  const carouselRef = useRef<HTMLDivElement>(null)

  // Helper function to calculate time ago
  const getTimeAgo = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    const diffWeeks = Math.floor(diffMs / 604800000)
    const diffMonths = Math.floor(diffMs / 2592000000)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`
    if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  useEffect(() => {
    fetchDashboardData()
  }, [user])

  // Guard: redirect unapproved alumni
  useEffect(() => {
    if (!user || user.role !== 'alumni') return
    supabaseClient
      .from('alumni_profiles')
      .select('approval_status')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data && data.approval_status !== 'approved') {
          navigate('/pending-approval')
        }
      })
  }, [user])

  const fetchDashboardData = async () => {
    if (!user) return
    
    try {
      setLoading(true)
      
      // Fetch surveys completed (count active and closed surveys, exclude archived)
      const { data: surveyResponses } = await supabaseClient
        .from('survey_responses')
        .select('survey_id, surveys!inner(status)')
        .eq('user_id', user.id)
      
      // Filter to include only active and closed (exclude archived)
      const filteredResponses = surveyResponses?.filter((r: any) => 
        r.surveys && (r.surveys.status === 'active' || r.surveys.status === 'closed')
      ) || []
      
      const completedSurveyIds = filteredResponses.map(r => r.survey_id)
      const totalCompletedCount = completedSurveyIds.length
      
      // Fetch alumni profile for completeness and graduation year
      const { data: profile } = await supabaseClient
        .from('alumni_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()
      
      if (profile) {
        setProfileData(profile)
        // Calculate years since graduation
        const gradYear = profile.year_graduated || new Date().getFullYear()
        const yearsSince = new Date().getFullYear() - gradYear
        
        // Fetch active surveys that this alumni is eligible for
        const { data: activeSurveysData } = await supabaseClient
          .from('surveys')
          .select('id, title, deadline, target_programs, target_batch, target_departments')
          .eq('status', 'active')
        
        // Filter surveys based on alumni's program, department, and graduation year
        const programCode = profile.program
        const yearGraduated = profile.year_graduated
        const department = profile.department
        
        const eligibleSurveys = activeSurveysData?.filter((survey: any) => {
          // Check department targeting
          const departmentMatch = !survey.target_departments || survey.target_departments.length === 0 || 
                                  (department && survey.target_departments.includes(department))
          
          // Check program targeting
          const programMatch = !survey.target_programs || survey.target_programs.length === 0 || 
                              (programCode && survey.target_programs.includes(programCode))
          
          // Check year targeting
          const yearMatch = !survey.target_batch || survey.target_batch.length === 0 || 
                           (yearGraduated && survey.target_batch.includes(yearGraduated))
          
          return departmentMatch && programMatch && yearMatch
        }) || []
        
        // Count how many eligible surveys have been completed
        const completedEligibleCount = eligibleSurveys.filter(survey => 
          completedSurveyIds.includes(survey.id)
        ).length
        
        const pendingCount = Math.max(0, eligibleSurveys.length - completedEligibleCount)

        const pendingDetails = eligibleSurveys
          .filter((survey: any) => !completedSurveyIds.includes(survey.id))
          .map((s: any) => ({ id: s.id, title: s.title || 'Untitled Survey', deadline: s.deadline || null }))
        setPendingSurveyList(pendingDetails)

        // Fetch active job applications count
        const { count: activeAppsCount } = await supabaseClient
          .from('job_applications')
          .select('*', { count: 'exact', head: true })
          .eq('alumni_id', user.id)
          .in('status', ['pending', 'reviewed', 'shortlisted'])

        setStats({
          surveysCompleted: totalCompletedCount,
          completedEligibleSurveys: completedEligibleCount,
          totalEligibleSurveys: eligibleSurveys.length,
          surveysPending: pendingCount,
          lastLogin: 'Today',
          yearsSinceGraduation: yearsSince >= 0 ? yearsSince : 0,
          activeApplications: activeAppsCount ?? 0
        })
      } else {
        setStats(prev => ({
          ...prev,
          surveysCompleted: totalCompletedCount,
          surveysPending: 0
        }))
      }
      
      // Fetch real recent activities
      const recentActivities: RecentActivity[] = []
      
      // Get recent survey completions (active and closed, exclude archived)
      const { data: recentSurveys, error: surveyError } = await supabaseClient
        .from('survey_responses')
        .select('submitted_at, survey_id, surveys!inner(status, title)')
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false })
        .limit(5) // Get more than we need, then filter
      
      if (!surveyError && recentSurveys) {
        // Filter to only active and closed surveys
        const filteredSurveys = recentSurveys.filter((r: any) => 
          r.surveys && (r.surveys.status === 'active' || r.surveys.status === 'closed')
        ).slice(0, 3) // Take only 3 after filtering
        
        filteredSurveys.forEach((response: any) => {
          const timestamp = new Date(response.submitted_at)
          const timeAgo = getTimeAgo(timestamp)
          recentActivities.push({
            id: `survey-${response.survey_id}`,
            description: `Completed "${(response.surveys as any)?.title || 'a survey'}"`,
            timestamp: timeAgo
          })
        })
      }
      
      // Get profile last updated time
      if (profile?.updated_at) {
        const updatedAt = new Date(profile.updated_at)
        const timeAgo = getTimeAgo(updatedAt)
        recentActivities.push({
          id: 'profile-update',
          description: 'Profile updated',
          timestamp: timeAgo
        })
      }
      
      // Limit to 5 most recent activities (survey responses already sorted by created_at desc)
      setActivities(recentActivities.slice(0, 5))

      // Fetch recent application status updates (last 30 days, non-pending/cancelled)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { data: appUpdatesData } = await supabaseClient
        .from('job_applications')
        .select('id, status, reviewed_at, jobs(title, companies(name))')
        .eq('alumni_id', user.id)
        .neq('status', 'pending')
        .neq('status', 'cancelled')
        .gte('reviewed_at', thirtyDaysAgo)
        .order('reviewed_at', { ascending: false })
        .limit(5)
      if (appUpdatesData) {
        setApplicationUpdates(
          appUpdatesData.map((a: any) => ({
            id: a.id,
            job_title: a.jobs?.title || 'Unknown Position',
            company_name: a.jobs?.companies?.name || 'Unknown Company',
            status: a.status,
            reviewed_at: a.reviewed_at,
          }))
        )
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const firstName = profileData?.first_name || (user?.profile as any)?.first_name || user?.email?.split('@')[0] || 'Alumni'
  const gradYear = profileData?.year_graduated
  const program = profileData?.program

  return (
    <div className="space-y-8">
      {/* Welcome Banner */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold">
              Welcome back, {firstName}!
            </h1>
            {gradYear && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-400/20 text-yellow-200">
                Class of {gradYear}
              </span>
            )}
          </div>
          <p className="text-blue-200 text-xs sm:text-sm">
            {program ? `${program} Alumni` : 'Alumni Dashboard'}
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {(() => {
              const empStatus = profileData?.current_employment_status
              const statusMap: Record<string, { label: string; cls: string }> = {
                employed: { label: 'Employed', cls: 'bg-green-400/25 text-green-100' },
                unemployed: { label: 'Seeking Employment', cls: 'bg-red-400/30 text-red-100' },
                'self-employed': { label: 'Self-Employed', cls: 'bg-yellow-400 text-gray-900' },
                studying: { label: 'Continuing Studies', cls: 'bg-purple-400/25 text-purple-100' },
                studying_employed: { label: 'Employed & Studying', cls: 'bg-teal-400/25 text-teal-100' },
              }
              const s = statusMap[empStatus] ?? { label: 'Status Not Set', cls: 'bg-white/10 text-white' }
              return (
                <span className={`rounded-full px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${s.cls}`}>
                  <Briefcase className="w-3.5 h-3.5" />
                  {s.label}
                </span>
              )
            })()}
            {(profileData?.field_of_work || profileData?.job_position) && (
              <span className="bg-white/10 rounded-full px-3 py-1.5 text-sm font-medium text-white flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-yellow-300" />
                {profileData.field_of_work || profileData.job_position}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-6">Overview</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          {/* Survey Progress */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Survey Progress</span>
            </div>
            <div className="px-5 py-4">
              {loading ? (
                <p className="text-3xl font-bold text-gray-900">—</p>
              ) : stats.totalEligibleSurveys > 0 ? (
                <p className="text-3xl font-bold text-gray-900">
                  {stats.completedEligibleSurveys}
                  <span className="text-xl text-gray-400 font-medium"> / {stats.totalEligibleSurveys}</span>
                </p>
              ) : (
                <p className="text-3xl font-bold text-gray-900">{stats.surveysCompleted}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                {loading ? '' : stats.totalEligibleSurveys > 0
                  ? 'Active eligible surveys done'
                  : 'Total surveys completed'}
              </p>
            </div>
          </div>

          {/* Pending Surveys */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Pending Surveys</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.surveysPending}</p>
              <p className="text-xs text-gray-400 mt-1">
                {loading ? '' : stats.surveysPending > 0 ? 'Awaiting your response' : 'All caught up!'}
              </p>
            </div>
          </div>

          {/* Active Applications */}
          <button
            onClick={() => navigate('/my-applications')}
            className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 text-left hover:shadow-md transition-shadow"
          >
            <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-white/80" />
              <span className="text-sm font-medium text-white/90">Active Applications</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.activeApplications}</p>
              <p className="text-xs text-gray-400 mt-1">
                {loading ? '' : stats.activeApplications > 0 ? 'In review or shortlisted' : 'No active applications'}
              </p>
            </div>
          </button>

          {/* Years Since Graduation */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-yellow-400 px-5 py-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-gray-800" />
              <span className="text-sm font-medium text-gray-800">Years Since Graduation</span>
            </div>
            <div className="px-5 py-4">
              <p className="text-3xl font-bold text-gray-900">{loading ? '—' : stats.yearsSinceGraduation}</p>
              <p className="text-xs text-gray-400 mt-1">
                {gradYear ? `Class of ${gradYear}` : 'Year not set in profile'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Updates */}
      {applicationUpdates.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Application Updates</h2>
            <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
              {applicationUpdates.length} update{applicationUpdates.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => navigate('/my-applications')}
              className="ml-auto flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 font-medium transition"
            >
              View all <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-2.5">
            {applicationUpdates.map(app => {
              const cfg = appUpdateConfig[app.status]
              if (!cfg) return null
              return (
                <button
                  key={app.id}
                  onClick={() => navigate('/my-applications')}
                  className={`w-full text-left rounded-xl border-l-4 ${cfg.border} ${cfg.bg} px-5 py-4 flex items-center justify-between hover:brightness-95 transition`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`shrink-0 ${cfg.color}`}>{cfg.icon}</span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{app.job_title}</p>
                      <p className="text-xs text-gray-500 truncate">{app.company_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color} ${cfg.bg}`}>
                      {cfg.icon} {cfg.label}
                    </span>
                    <span className="text-xs text-gray-400 hidden sm:block">{getTimeAgo(new Date(app.reviewed_at))}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* My Pending Surveys */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">My Pending Surveys</h2>
          {pendingSurveyList.length > 0 && (
            <span className="px-2.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
              {pendingSurveyList.length} due
            </span>
          )}
          {pendingSurveyList.length > 1 && (
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={() => carouselRef.current?.scrollBy({ left: -280, behavior: 'smooth' })}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={() => carouselRef.current?.scrollBy({ left: 280, behavior: 'smooth' })}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          )}
        </div>
        {pendingSurveyList.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-gray-700 font-medium">All caught up!</p>
            <p className="text-sm text-gray-400 mt-1">You have no pending surveys at the moment.</p>
          </div>
        ) : (
          <div
            ref={carouselRef}
            className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
            style={{ scrollbarWidth: 'none' }}
          >
            {pendingSurveyList.map((survey) => {
              const daysLeft = survey.deadline
                ? Math.ceil((new Date(survey.deadline).getTime() - Date.now()) / 86400000)
                : null
              const isUrgent = daysLeft !== null && daysLeft <= 3
              return (
                <button
                  key={survey.id}
                  onClick={() => navigate('/survey')}
                  className="text-left group flex-none w-64 sm:w-72 snap-start"
                >
                  <div className={`rounded-xl p-5 shadow-sm hover:shadow-md transition-all border-l-4 h-full ${
                    isUrgent ? 'bg-red-50 border-red-400' : 'bg-yellow-50 border-yellow-400'
                  }`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isUrgent ? 'bg-red-400' : 'bg-yellow-400'
                      }`}>
                        <ClipboardList className={`w-5 h-5 ${isUrgent ? 'text-white' : 'text-gray-800'}`} />
                      </div>
                      {daysLeft !== null && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isUrgent ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {daysLeft === 0 ? 'Due today' : `${daysLeft}d left`}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-gray-900 text-sm line-clamp-2">{survey.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {survey.deadline
                        ? `Due ${new Date(survey.deadline).toLocaleDateString()}`
                        : 'No deadline'}
                    </p>
                    <p className="text-xs text-blue-700 mt-3 font-medium group-hover:underline">Take Survey →</p>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="bg-blue-900 px-5 py-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-white/80" />
            <span className="text-sm font-medium text-white/90">Recent Activity</span>
          </div>
          {activities.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {activities.map((activity) => (
                <div key={activity.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-gray-50">
                  <p className="text-sm text-gray-800">{activity.description}</p>
                  <p className="text-xs text-gray-400 ml-4 shrink-0">{activity.timestamp}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-10 text-center">
              <p className="text-gray-500">No recent activity yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Your survey completions and profile updates will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DashboardPage